import { ref, reactive, watch } from 'vue';
import { debounce } from '@/services/utils';
import v2CollectionManager from '@/services/v2CollectionManager';
import Admin from '@/services/admins';

export function useAdminManagement(staffSchoolViewEnabledRef, schoolIdRef, currentUserRef) {
    const admins = reactive(v2CollectionManager.create({
        listType: 'admins',
        paginationOptions: {
            size: 25
        },
        filters: {
            schoolId: schoolIdRef.value
        }
    }));
    const isLoading = ref(false);
    const searchText = ref('');
    const executeFilter = ref(() => {});
    const selectedAdmins = ref([]);

    // --- Helper Functions ---
    const processAdminList = () => {
        admins.list = admins.list.map((admin) => ({
            ...admin,
            adminAccount: admin.adminAccount || {},
            canAccessKb: admin.adminAccount?.canAccessKb ?? false,
            name: admin.displayName || `${admin.firstName || ''} ${admin.lastName || ''}`.trim(),
            firstName: admin.firstName || '',
            lastName: admin.lastName || '',
            sendingInvite: admin.sendingInvite || false
        }));
    };

    // --- Core Logic ---
    const loadMoreAdmins = async () => {
        await admins.addNextPage();
        processAdminList();
    };

    const refreshAdmins = async () => {
        isLoading.value = true;
        if (staffSchoolViewEnabledRef?.value && schoolIdRef?.value) {
            admins.filters.schools = schoolIdRef.value;
        } else {
            delete admins.filters.schools;
        }
        await admins.refresh();
        processAdminList();
        isLoading.value = false;
    };

    const setupAdminSearch = () => {
        executeFilter.value = debounce(async () => {
            admins.filters.search = searchText.value;
            await refreshAdmins();
        }, 700);

        watch(searchText, () => {
            if (typeof executeFilter.value === 'function') {
                executeFilter.value();
            }
        });
    };

    const resendInvite = async (adminId) => {
        const adminInList = admins.list.find((a) => a.id === adminId);
        if (!adminInList) {
            console.error('resendInvite (composable): Admin not found in list.', adminId);
            throw new Error('Admin not found in the list.');
        }

        try {
            adminInList.sendingInvite = true;
            await Admin.api.reInviteCoadmin(adminInList.email);
            return adminInList.email;
        } catch (error) {
            console.error('Failed to resend invite (composable):', error);
            throw error;
        } finally {
            if (adminInList) {
                adminInList.sendingInvite = false;
            }
        }
    };

    const removeAdmins = async (adminIds) => {
        if (!adminIds || adminIds.length === 0) {
            return {
                success: false,
                error: 'No admin IDs provided for removal.',
                numRemoved: 0,
                selfDeactivationAttempted: false
            };
        }

        const currentUserId = currentUserRef.value?.id;
        let selfDeactivationAttempted = false;
        let actualAdminIdsToDeactivate = [...adminIds];

        if (currentUserId && adminIds.includes(currentUserId)) {
            selfDeactivationAttempted = true;
            actualAdminIdsToDeactivate = adminIds.filter((id) => id !== currentUserId);

            if (actualAdminIdsToDeactivate.length === 0) {
                // Only self was selected
                return {
                    success: true,
                    numRemoved: 0,
                    selfDeactivationAttempted: true,
                    message: 'Cannot deactivate self.'
                };
            }
        }

        try {
            await Admin.api.deactivate(actualAdminIdsToDeactivate);
            await refreshAdmins();
            selectedAdmins.value = selectedAdmins.value.filter(admin => !actualAdminIdsToDeactivate.includes(admin.id));

            return {
                success: true,
                numRemoved: actualAdminIdsToDeactivate.length,
                selfDeactivationAttempted: selfDeactivationAttempted,
                message: `${actualAdminIdsToDeactivate.length} admin(s) removed successfully.`
            };
        } catch (error) {
            console.error('Failed to remove admins (composable):', error);
            return {
                success: false,
                error: error,
                numRemoved: 0,
                selfDeactivationAttempted: selfDeactivationAttempted
            };
        }
    };

    // --- Selection Logic (Moved from component) ---
    const selectAdmin = (admin) => {
        if (!selectedAdmins.value.find((item) => item.id === admin.id)) {
            selectedAdmins.value.push(admin);
        }
    };

    const unselectAdmin = (admin) => {
        selectedAdmins.value = selectedAdmins.value.filter((item) => item.id !== admin.id);
    };

    const toggleAdminSelection = (admin, checked) => {
        if (checked) {
            selectAdmin(admin);
        } else {
            unselectAdmin(admin);
        }
    };

    // --- Display Formatting (Moved from component) ---
    const formatDisplayName = (data) => {
        if (!data.firstName && !data.lastName) {
            return '';
        }

        return `${data.firstName || ''} ${data.lastName || ''}`.trim();
    };

    const formatDisplayPhoneNumber = (adminData) => {
        const phoneNumber = adminData.phoneNumber;

        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        if (!cleaned) {
            return 'Not provided';
        }

        const match = cleaned.match(/^(?:1)?(\d{3})(\d{3})(\d{4})$/);
        if (!match) {
            return 'Invalid number';
        }

        return `(${match[1]}) ${match[2]}-${match[3]}`;
    };

    return {
        admins,
        isLoading,
        searchText,
        selectedAdmins,
        loadMoreAdmins,
        refreshAdmins,
        setupAdminSearch,
        resendInvite,
        removeAdmins,
        toggleAdminSelection,
        formatDisplayName,
        formatDisplayPhoneNumber
    };
}
